import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import {
  GalleryItemInterface,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import GalleryItem from '@components/gallery-item'
import GallerySkeleton from '@components/gallery-skeleton'
import GalleryTab from '@components/gallery-tab'

import {
  selectFromResult as selectFromGalleryResult,
  useGetGalleryQuery,
} from '@api/gallery'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { PlaySvg, StopSvg } from '@svg/react'

interface PagePropsInterface {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
}

const GALLERY_PAGE_ROUTE = '/gallery'

function Gallery({ session, projectIdentity }: PagePropsInterface) {
  const history = useHistory()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [slideShowState, setSlideShowState] = useState(false)
  const [transitionEffectClass, setTransitionEffectClass] = useState('')

  const galleryPayload = useGetGalleryQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult: selectFromGalleryResult }
  )

  const tabItems: Array<string> = useMemo(() => {
    const { galleries = [] } = galleryPayload

    if (galleries.length < 1) {
      return []
    }

    return galleries.map((item) => item.label || '')
  }, [galleryPayload])

  const galleryItems: Array<GalleryItemInterface> = useMemo(() => {
    const { galleries = [] } = galleryPayload

    if (galleries.length < 1) {
      return []
    }

    return galleries[activeTabIndex].items || []
  }, [galleryPayload, activeTabIndex])

  const handleOnTabChange = useCallback(
    async (index: number) => {
      setTransitionEffectClass(
        index > activeTabIndex
          ? 'transition-slide-left'
          : 'transition-slide-right'
      )
      setActiveTabIndex(index)
      setActiveSlideIndex(0)
      setSlideShowState(false)

      await firebaseControlQuery.update({
        [`design.galleryControlV2.activeTabIndex`]: index,
        [`design.galleryControlV2.activeSlideIndex`]: 0,
        [`design.galleryControlV2.autoPlayState`]: false,
      })
    },
    [activeTabIndex, firebaseControlQuery]
  )

  const handleOnItemClick = useCallback(
    async (event: MouseEvent<HTMLDivElement>, index: number) => {
      event.preventDefault()
      setActiveSlideIndex(index)
      setSlideShowState(false)

      await firebaseControlQuery.update({
        [`design.galleryControlV2.activeSlideIndex`]: index,
        [`design.galleryControlV2.autoPlayState`]: false,
      })
    },
    [firebaseControlQuery]
  )

  const handleOnSlideShowClick = useCallback(async () => {
    setSlideShowState(!slideShowState)
    await firebaseControlQuery.update({
      [`design.galleryControlV2.autoPlayState`]: !slideShowState,
    })
  }, [slideShowState, firebaseControlQuery])

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      design: {
        galleryControlV2: {
          activeTabIndex: activeTabIndexFirebase,
          activeSlideIndex: activeSlideIndexFirebase,
          autoPlayState: autoPlayStateFirebase,
        },
      },
    } = session

    setActiveTabIndex(activeTabIndexFirebase)
    setActiveSlideIndex(activeSlideIndexFirebase)
    setSlideShowState(autoPlayStateFirebase)
  }, [session?.design.galleryControlV2])

  useEffect(
    () => () => {
      const currentRoute = history.location.pathname || ''
      if (currentRoute !== GALLERY_PAGE_ROUTE) {
        firebaseControlQuery.update({
          'design.galleryControlV2.activeTabIndex': 0,
          'design.galleryControlV2.activeSlideIndex': 0,
          'design.galleryControlV2.autoPlayState': false,
        })
      }
    },
    [firebaseControlQuery]
  )

  return (
    <Container>
      <DataHandler
        payloads={{ ...galleryPayload, data: galleryPayload.galleries }}
        skeletonFrame={<GallerySkeleton hasTabBar />}
      >
        <div className="h-full w-full">
          <div className="relative flex h-top-bar w-full items-center justify-center px-5 text-neutralColour">
            <div className="text-title font-medium">Gallery</div>
            {galleryItems.length > 1 && (
              <div className="absolute bottom-14 right-5">
                <button
                  type="button"
                  disabled={false}
                  onClick={handleOnSlideShowClick}
                  className="inline-flex cursor-pointer items-center gap-2.5 text-default opacity-100"
                >
                  <span>{slideShowState ? 'Stop' : 'Start'} slideshow</span>{' '}
                  {slideShowState ? <StopSvg /> : <PlaySvg />}
                </button>
              </div>
            )}
          </div>
          <GalleryTab
            items={tabItems}
            activeGalleryTabIndex={activeTabIndex}
            onTabChange={handleOnTabChange}
            className="mb-[15px] flex-shrink-0"
          />

          <div className="no-scrollbar h-transition-body w-full overflow-auto">
            <TransitionGroup className="h-full w-full">
              <CSSTransition
                key={activeTabIndex}
                classNames={transitionEffectClass}
                timeout={500}
                exit={false}
                onEntered={(node: HTMLElement) =>
                  node.classList.remove('transition-slide-left-enter-done')
                }
              >
                <div className="grid grid-cols-3 gap-4 px-4">
                  {galleryItems.map((item, index) => (
                    <GalleryItem
                      key={index as number}
                      item={item}
                      isActive={index === activeSlideIndex}
                      onItemClick={(event: MouseEvent<HTMLDivElement>) =>
                        handleOnItemClick(event, index)
                      }
                    />
                  ))}
                </div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </div>
      </DataHandler>
    </Container>
  )
}

const mapStateToProps = ({
  firestore: { session },
  projectIdentity,
}: RootStateTypeExtra) => ({
  session,
  projectIdentity,
})

export default connect(mapStateToProps)(Gallery)
